import EmptyState from 'components/EmptyState';
import { useIsMobile } from 'hooks/useIsMobile';
import HomeSection from 'pages/home/HomeSection';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetWidgetCatalogueQuery } from 'store/api/homeWidgetFigures';
import type { WidgetFigure } from 'types/homeWidgetFigures';
import type { HomeWidgetDefinition, HomeWidgetForm, HomeWidgetPeriod, HomeWidgetSize } from 'types/homeWidgets';
import { cn } from 'utils/cn';
import { BreakdownBars } from './BreakdownBars';
import { BreakdownDonut } from './BreakdownDonut';
import { useFigureFormat } from './figureFormat';
import { ForbiddenState, type KpiFormProps } from './KpiParts';
import { KpiSkeleton } from './KpiSkeleton';
import { NumberForm } from './NumberForm';
import { ProgressForm } from './ProgressForm';
import { RankedForm } from './RankedForm';
import { TrendForm } from './TrendForm';
import { useWidgetFigure, WidgetFigureState } from './useWidgetFigure';
import { FAMILY_LIST_LABEL, widgetLinks } from './widgetLink';

const PERIOD_LABEL: Record<HomeWidgetPeriod, string> = {
    TODAY: 'home.widgets.period.today',
    THIS_WEEK: 'home.widgets.period.this-week',
    THIS_MONTH: 'home.widgets.period.this-month',
    YEAR_TO_DATE: 'home.widgets.period.year-to-date',
    LAST_12_MONTHS: 'home.widgets.period.last-12-months',
    AS_SAVED: 'home.widgets.period.as-saved',
};

function BreakdownForm(props: KpiFormProps) {
    const { definition } = props;
    const isDonut = definition.source === 'CATALOGUE' && definition.drawnAs === 'DONUT';
    return isDonut ? <BreakdownDonut {...props} /> : <BreakdownBars {...props} />;
}

const FORM: Record<HomeWidgetForm, (props: KpiFormProps) => ReactNode> = {
    NUMBER: NumberForm,
    PROGRESS: ProgressForm,
    TREND: TrendForm,
    BREAKDOWN: BreakdownForm,
    RANKED: RankedForm,
};

/** A Number and a Progress show their zero; any other form with nothing to draw says so instead. */
function hasNothingToShow(form: HomeWidgetForm, figure: WidgetFigure) {
    if (form === 'TREND') return !figure.points?.some((point) => point.value !== 0);
    if (form === 'BREAKDOWN') return !figure.rows?.length && !figure.otherValue;
    return form === 'RANKED' && !figure.rows?.length;
}

type Props = {
    /** A stored widget, or one still being built: the name typed so far and the definition as it stands. */
    widget: { id?: number; name: string; definition: HomeWidgetDefinition };
    /** The size it is drawn at, which on Home is its entry's and can differ from the one its author chose. */
    size: HomeWidgetSize;
    /**
     * The creator's live preview: the figure is the definition's as it stands, not the stored widget's, and nothing
     * in the card leads away from the dialog.
     */
    preview?: boolean;
    /** The end of the header: the menu of an own widget, the lock of one the firm placed. */
    actions?: ReactNode;
    /** Where the guided tour points: the card under this name, its figure under the same with `-figure`. */
    'data-tour'?: string;
};

/**
 * A widget somebody built, as a Home card: its name on one line, its period, then its figure in the form and at the
 * size asked for, or what stands in for the figure while it loads, is closed to the viewer, or failed. Every state
 * keeps the header and the card's footprint, so the grid never moves. On a phone the grid is one column, where a
 * two-column widget is one column wide: it is drawn as its form draws one column.
 */
export function KpiWidget({ widget, size, preview = false, actions, 'data-tour': tourTarget }: Props) {
    const { name, definition } = widget;
    const state = useWidgetFigure(definition, preview ? undefined : widget.id);
    const small = size === '1x1';
    const isMobile = useIsMobile();
    const drawnAt = isMobile && size === '2x2' ? '1x2' : size;
    return (
        <HomeSection
            // The name has no width of its own, so the header's other items never wrap under a long one: it
            // takes what they leave and ends in an ellipsis.
            title={<span className="block w-0 min-w-full truncate">{name}</span>}
            actions={
                <>
                    <span className="text-muted-foreground text-caption whitespace-nowrap">
                        <FormattedMessage id={PERIOD_LABEL[definition.period]} />
                    </span>
                    {actions}
                </>
            }
            className={small ? 'gap-0 pt-[7px] pb-2.5' : 'gap-1.5 pt-2.5 pb-3.5'}
            data-tour={tourTarget}
        >
            <div
                aria-busy={state.isLoading}
                data-tour={tourTarget && `${tourTarget}-figure`}
                className={cn(
                    'flex min-h-0 flex-1 flex-col transition-opacity',
                    small ? 'gap-0.5' : 'gap-1.5',
                    state.isRefreshing && 'opacity-35'
                )}
            >
                <KpiBody name={name} definition={definition} size={drawnAt} state={state} preview={preview} />
            </div>
        </HomeSection>
    );
}

type BodyProps = {
    name: string;
    definition: HomeWidgetDefinition;
    size: HomeWidgetSize;
    state: WidgetFigureState;
    preview: boolean;
};

function KpiBody({ name, definition, size, state, preview }: BodyProps) {
    const intl = useIntl();
    const small = size === '1x1';
    if (state.isForbidden) {
        // A view can be closed to someone its list is open to, so a saved-view widget names no access to ask for.
        const area = definition.source === 'CATALOGUE' ? FAMILY_LIST_LABEL[definition.family] : undefined;
        return <ForbiddenState area={area && intl.formatMessage({ id: area })} small={small} />;
    }
    if (state.isError) {
        return (
            <EmptyState
                size="inline"
                variant="error"
                errorTitleId="home.widgets.state.error.title"
                onRetry={state.retry}
                // One row in the one-row card, which has no room for the button under the line.
                className={cn('flex-1 justify-center', small && 'flex-row justify-between p-0 text-start')}
            />
        );
    }
    if (!state.figure) {
        return <KpiSkeleton size={size} />;
    }
    if (hasNothingToShow(definition.form, state.figure)) {
        return <EmptyState size="inline" titleId="home.widgets.state.empty.title" className="flex-1 justify-center" />;
    }
    return (
        <KpiFigure
            name={name}
            definition={definition}
            size={size}
            figure={state.figure}
            state={state}
            preview={preview}
        />
    );
}

type FigureProps = {
    name: string;
    definition: HomeWidgetDefinition;
    size: HomeWidgetSize;
    figure: WidgetFigure;
    state: WidgetFigureState;
    preview: boolean;
};

/**
 * The figure in its form. A catalogue widget's links are built from the catalogue, a saved-view widget's from the
 * view its figure counted; a preview has no links and asks for neither.
 */
function KpiFigure({ name, definition, size, figure, state, preview }: FigureProps) {
    const { data: catalogue } = useGetWidgetCatalogueQuery(undefined, {
        skip: preview || definition.source !== 'CATALOGUE',
    });
    const format = useFigureFormat(figure);
    const links = preview ? undefined : widgetLinks(catalogue, definition, figure.period, state.view);
    const Form = FORM[definition.form];
    return (
        <Form
            name={name}
            definition={definition}
            figure={figure}
            size={size}
            links={links}
            format={format}
            capped={state.isCapped}
        />
    );
}
